import { Component, HostBinding, ViewChildren, ViewChild, QueryList, ViewEncapsulation } from '@angular/core';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { HelpIndex, Endpoint, SettingsType } from '../../../../models/enums';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { NgForm } from '@angular/forms';
import { SearchSettingsViewModel, SearchSettings, SearchTermMetrics } from '../../../../models/search-settings';
import { ActionType, ObjectActionListType, Permissions } from '../../../../models/permission';
import { DrObject } from '../../../../models/dr-object';

@Component({
    standalone: false,
    selector: 'res-search-settings',
    templateUrl: './search-settings.component.html',
    styleUrls: ['./search-settings.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class SearchSettingsComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChildren(GenericGridComponent) genericGrids: QueryList<GenericGridComponent>;
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;
    @ViewChild('contentForm', { static: true }) contentForm: NgForm;

    handle: string;
    isBusyMessage: string;
    validChanges = true;
    automationId = 'search-settings';
    viewModel: SearchSettingsViewModel;
    searchTermMetricsFields = [];
    helpIndex: HelpIndex;
    isBusy = false;
    hasPermissions = true;
    maxCustomNameLength = DrObject.DefaultMaximumNameCharacters;
    tokenizationVersion: string;
    isProjectSettings = false;
    isSystemUser = false;

    // Methods
    get isDirty(): boolean {
        if (this.contentForm || this.viewModel.isDirty()) {
            return this.contentForm.dirty;
        }
        return false;
    }

    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
            }
        }
    }

    constructor(private commonService: CommonService, private restService: RestService) {
        this.viewModel = new SearchSettingsViewModel(new SearchSettings());
    }

    discardChanges(): void {
        this.isDirty = false;
        this.loadSearchSettingsData();
    }

    selectedTreeItemChanged(treeItem: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.handle = projectHandle ? projectHandle : treeItem.data.handle;
        if (projectHandle) {
            this.isProjectSettings = true;
            if (this.commonService.tokenizerConfigVersion) {
                this.tokenizationVersion = this.commonService.tokenizerConfigVersion;
            } else {
                this.restService.post(Endpoint.projectManager_getProjectConfig, { projectId: this.handle }).then(response => {
                    if (response && response.parsingConfiguration && response.parsingConfiguration.tokenizerConfig) {
                        this.tokenizationVersion = response.parsingConfiguration.tokenizerConfig.scheme === 1 ? 'V1' : 'V2';
                    }
                });
            }
        }
        this.isDirty = false;
        this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
        const permissionType = projectHandle ? ObjectActionListType.PRJ_SEARCH_SETTINGS : this.isSystemUser ? ObjectActionListType.SYSTEM_SEARCH_SETTINGS : ObjectActionListType.ORG_SEARCH_SETTINGS;
        this.helpIndex = projectHandle ? HelpIndex.myedisc_case_search_settings_dlg : (settingsType === SettingsType.system ? HelpIndex.sys_template_search_settings_dlg : HelpIndex.org_search_options_template_dlg);
        this.hasPermissions = Permissions.checkUserPermissions([{ permissionType: permissionType, permissionActions: [ActionType.CREATE] }]);
        this.loadSearchSettingsData();
    }

    loadSearchSettingsData(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Loading Search Settings...';
        this.restService.post(Endpoint.projectManager_getSearchSettings, { handle: this.handle, requestHandle: this.handle, systemScope: this.isSystemUser }).then(responseAllFields => {
            this.isBusy = false;
            if (responseAllFields && responseAllFields.requestHandle === this.handle && responseAllFields.searchSettings) {
                this.viewModel = new SearchSettingsViewModel(responseAllFields.searchSettings);
                this.searchTermMetricsFields = [];
                responseAllFields.searchSettings.searchTermMetrics.forEach((element, index) => {
                    element.index = index + 1;
                    this.searchTermMetricsFields.push(element);
                    if (element.defaultLabel === 'Clause') {
                        element.editEnable = true;
                    } else {
                        element.editEnable = false;
                    }
                });
            }
        }, () => {
            this.isBusy = false;
        });
    }

    onFieldDrop(draggedEl: any, onElementDragged: any): void {
        if (draggedEl.data && onElementDragged) {
            const onDraggedEl = draggedEl.data.index - 1;
            let onDroppedEl = null;
            if (onElementDragged === 'dropOnLastPosition') {
                onDroppedEl = this.searchTermMetricsFields.length;
            } else {
                onDroppedEl = onElementDragged.index - 1;
            }
            let index = onDroppedEl;
            this.searchTermMetricsFields.splice(onDraggedEl, 1);
            index = onDraggedEl < onDroppedEl ? index - 1 : index;
            this.searchTermMetricsFields.splice(index, 0, (draggedEl.data));
            let columnNumber = 0;
            this.searchTermMetricsFields.forEach(entry => {
                entry.index = columnNumber + 1;
                entry.position = columnNumber;
                columnNumber++;
            });
            this.isDirty = this.viewModel.isDirty();
        }
    }

    trackByFunction(index: number, item: any) {
        return item.index;
    }

    saveChanges(fromAutoSave = false): Promise<boolean> {
        this.viewModel.model.searchTermMetrics = this.searchTermMetricsFields;
        this.viewModel.model.searchTermMetrics.forEach(entry => {
            delete entry.index;
            delete entry.editEnable;
        });

        return new Promise<boolean>((resolve) => {
            this.isBusy = true;
            this.isBusyMessage = 'Saving ...';
            this.restService.post(Endpoint.projectManager_setSearchSettings, {
                handle: this.handle,
                includeBatchSheets: this.viewModel.model.includeBatchSheets,
                includeCustodianSheets: this.viewModel.model.includeCustodianSheets,
                searchDialect: "STANDARD",
                searchTermMetrics: this.viewModel.model.searchTermMetrics,
                systemScope: this.isSystemUser
            }).then(() => {
                this.isBusy = false;
                if (!fromAutoSave) {
                    this.loadSearchSettingsData();
                }
                resolve(true);
                this.isDirty = false;
                this.commonService.searchDialect = this.viewModel.model.searchDialect;
            }, () => {
                this.isBusy = false;
                resolve(false);
            });
        });
    }

    onChange(): void {
        this.isDirty = this.viewModel.isDirty();
    }

    openHelp = (): void => this.commonService.openHelp(this.helpIndex);

    isDisabled(): boolean {
        let isDisabled = true;
        if (!this.isBusy && this.isDirty) {
            isDisabled = false;
        }
        return isDisabled;
    }

    onCustomLabelChange(entry: SearchTermMetrics): void {
        if (entry && entry.label) {
            entry.label = entry.label.replace(/(\r\n|\n|\r)/gi, ' ');
            entry.label.trim();
            entry.label = this.maxCustomNameLength ? entry.label.substring(0, this.maxCustomNameLength) : entry.label;
        }
        this.isDirty = this.viewModel.isDirty();
    }
}
